import { useEffect } from "react";
import { useQuery } from "@apollo/client";
import classNames from "classnames";
import AppLayout from "@/layouts/AppLayout";
import PageHeading from "@/components/PageHeading";
import usePageTitle from "@/hooks/usePageTitle";
import { useDenyAuthorization, useHasRoles } from "@/hooks/useCapabilities";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { ROLE } from "@/providers/AuthorizationProvider/types";
import { useAssumeUser } from "@/providers/AssumeUserProvider";
import AgentChatLogsViewer from "@/features/agent-chat-logs/AgentChatLogsViewer";
import { canAccessAgentChatLogs } from "@/features/agent-chat-logs/access";
import { AGENT_CHAT_CONVERSATIONS_QUERY } from "@/features/agent-chat-logs/queries";

export default function AgentChatLogsPage() {
  usePageTitle("Chat Logs");
  const denyAuthorization = useDenyAuthorization();
  const isGlobalAdmin = useHasRoles([ROLE.GLOBAL_ADMIN]);
  const canView = canAccessAgentChatLogs(isGlobalAdmin);
  const { assumedUser, impersonationError } = useAssumeUser();
  const { isCompactLayout } = useIsCompactLayout();
  const showAssumeUserBanner = !!assumedUser || !!impersonationError;
  const reserveBottomChrome = !isCompactLayout;
  const heightClassName = classNames({
    "h-[calc(100dvh-5.5rem)]": !showAssumeUserBanner && !reserveBottomChrome,
    "h-[calc(100dvh-5.5rem-6rem)]": !showAssumeUserBanner && reserveBottomChrome,
    "h-[calc(100dvh-9.5rem)]": showAssumeUserBanner && !reserveBottomChrome,
    "h-[calc(100dvh-9.5rem-6rem)]": showAssumeUserBanner && reserveBottomChrome
  });
  const { data, loading, error } = useQuery(AGENT_CHAT_CONVERSATIONS_QUERY, {
    variables: { first: 50 },
    skip: !canView,
    fetchPolicy: "cache-and-network"
  });

  useEffect(() => {
    if (!canView) {
      denyAuthorization();
    }
  }, [canView, denyAuthorization]);

  if (!canView) {
    return null;
  }

  return (
    <AppLayout>
      <div className={classNames("mx-auto flex w-full min-h-0 flex-col pt-4 lg:pt-6", heightClassName)}>
        <PageHeading title="Chat Logs" containerClassName="mb-4 pt-0" />
        <AgentChatLogsViewer
          conversations={data?.agentChatConversations?.nodes ?? []}
          loading={loading}
          error={!!error}
        />
      </div>
    </AppLayout>
  );
}
